import { useState } from 'react'
import type { JSX, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { IconButtonProps } from '@buffer-mono/popcorn'

import { useSplitEnabled } from '@buffer-mono/features'
import { IconButton, NoteBlankIcon, NotePlusIcon } from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { usePostData } from '~publish/components/PostCard/PostCardContext'
import { usePostDetailsDialog } from '~publish/components/PostDialogs/helpers'
import {
  PostGroupDialog,
  POST_GROUPS_UI_FLAG,
} from '~publish/components/PostDialogs/PostGroupDialog'
import { NotesVisibilityHint } from '~publish/components/PostNotes/NotesVisibilityHint'
import {
  NotesIndicator,
  NotesPopover,
  NotesPopoverActions,
  NotesPopoverExpandButton,
  NotesPopoverHeader,
  NotesPopoverTitle,
  getNotesIndicatorLabel,
  getNotesUnreadTooltip,
} from '~publish/components/Notes'
import type {
  NotesIndicatorAnchor,
  NotesPopoverTriggerProps,
} from '~publish/components/Notes'
import { CreatePostNoteForm } from '~publish/components/PostNotes/CreatePostNoteForm'
import { FocusAfterDelete } from '~publish/components/PostNotes/FocusAfterDelete'
import { PostNotesFeed } from '~publish/components/PostNotes/PostNotesFeed'
import { NotesFeedActions } from '~publish/components/PostNotes/NotesFeedActions'
import { PostNotesProvider } from '~publish/components/PostNotes/PostNotesProvider'
import { usePostNotesFrame } from '~publish/components/PostNotes/usePostNotesFrame'
import { SplitTagBadge } from '~publish/components/SplitTagBadge/SplitTagBadge'
import { COLLABORATION_NOTES_SPLIT } from '~publish/components/PostDialogs/PostDetailsContent/useCollaborationNotesPanel'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import {
  usePostNoteTotal,
  usePostUnreadMentionCount,
  usePostUnreadNoteCount,
} from '~publish/hooks/notes/usePostNoteCounts'

const EXPAND_CTA = 'publish-queue-notesPopover-expand-1'

type EntryPointProps = Pick<
  IconButtonProps,
  'variant' | 'size' | 'className'
> & {
  /**
   * Queue cards open the feed here. Leave unset for the direct Post Details
   * or group-dialog path the calendar icon still uses.
   */
  openInPopover?: boolean
}

export function PostNotesEntryPoint({
  variant,
  size,
  className,
  openInPopover = false,
}: EntryPointProps): JSX.Element {
  if (openInPopover) {
    return (
      <QueuePostNotesEntry
        variant={variant}
        size={size}
        className={className}
      />
    )
  }
  return (
    <DirectPostNotesEntry variant={variant} size={size} className={className} />
  )
}

// IconButton falls back to `secondary`, which is bordered.
const notesIndicatorAnchor = (
  variant: IconButtonProps['variant'],
): NotesIndicatorAnchor => (variant === 'tertiary' ? 'icon' : 'button')

function useTrackNotesOpened(): () => void {
  const post = usePostData()
  const organizationId = useOrganizationId()
  return (): void => {
    if (!organizationId) return
    BufferTracker.noteFeedViewed({
      entityId: post.id,
      entityType: 'post',
      channel: post.channel.service,
      channelId: post.channel.id,
      channelType: post.channel.type,
      channelUsername: post.channel.displayName ?? '',
      clientName: 'publishWeb',
      entityStatus: post.status || null,
      entityText: post.text || '',
      organizationId,
    })
  }
}

function useGroupNotesDialog(): boolean {
  const { isReady, isEnabled } = useSplitEnabled(POST_GROUPS_UI_FLAG)
  return isReady && isEnabled
}

function DirectPostNotesEntry({
  variant,
  size,
  className,
}: Pick<IconButtonProps, 'variant' | 'size' | 'className'>): JSX.Element {
  const post = usePostData()
  const trackOpen = useTrackNotesOpened()
  const { constructDetailsDialogLocation } = usePostDetailsDialog({
    postId: post.id,
  })
  const groupUi = useGroupNotesDialog()
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const unreadCount = usePostUnreadNoteCount(post.id)
  const label = getNotesIndicatorLabel(unreadCount)
  const tooltip = getNotesUnreadTooltip(
    unreadCount,
    usePostUnreadMentionCount(post.id),
  )
  const content = (
    <NotesIndicator
      anchor={notesIndicatorAnchor(variant)}
      unreadCount={unreadCount}
    >
      <NoteBlankIcon />
    </NotesIndicator>
  )

  if (groupUi) {
    return (
      <PostGroupDialog
        postId={post.id}
        postGroupId={post.postGroup?.id}
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
        focusNotesComposer
        trigger={
          <IconButton
            className={className}
            label={label}
            tooltip={tooltip}
            onClick={trackOpen}
            size={size}
            type="button"
            variant={variant}
          >
            {content}
          </IconButton>
        }
      />
    )
  }

  const detailsDialogLocation = constructDetailsDialogLocation({
    cta: 'publish-post-notes-entryPoint-1',
    notesFocus: 'true',
  })

  return (
    <IconButton
      as={Link}
      className={className}
      label={label}
      tooltip={tooltip}
      onClick={trackOpen}
      size={size}
      to={detailsDialogLocation}
      variant={variant}
    >
      {content}
    </IconButton>
  )
}

function QueuePostNotesEntry({
  variant,
  size,
  className,
}: NotesPopoverTriggerProps): JSX.Element {
  const post = usePostData()
  const trackOpen = useTrackNotesOpened()
  const groupUi = useGroupNotesDialog()
  const [groupDialogOpen, setGroupDialogOpen] = useState(false)
  const unreadCount = usePostUnreadNoteCount(post.id)
  const unreadMentionCount = usePostUnreadMentionCount(post.id)
  // An unknown (`null`) total keeps the trigger visible: a create made it
  // unknown, or this screen's query did not select it.
  const empty = usePostNoteTotal(post.id) === 0

  return (
    <>
      <NotesPopover
        variant={variant}
        size={size}
        className={className}
        label={getNotesIndicatorLabel(unreadCount)}
        tooltip={getNotesUnreadTooltip(unreadCount, unreadMentionCount)}
        icon={
          <NotesIndicator
            anchor={notesIndicatorAnchor(variant)}
            unreadCount={unreadCount}
          >
            {empty ? <NotePlusIcon /> : <NoteBlankIcon />}
          </NotesIndicator>
        }
        onOpen={trackOpen}
        data-testid="post-notes-popover"
        data-notes-empty={empty}
      >
        <PostNotesPopoverThread
          expand={
            groupUi ? (
              <NotesPopoverExpandButton
                onClick={() => setGroupDialogOpen(true)}
              />
            ) : (
              <PostNotesExpandLink />
            )
          }
        />
      </NotesPopover>
      {groupUi && (
        <PostGroupDialog
          postId={post.id}
          postGroupId={post.postGroup?.id}
          open={groupDialogOpen}
          onOpenChange={setGroupDialogOpen}
          focusNotesComposer
        />
      )}
    </>
  )
}

function PostNotesExpandLink(): JSX.Element {
  const post = usePostData()
  const { constructDetailsDialogLocation } = usePostDetailsDialog({
    postId: post.id,
  })
  return (
    <NotesPopoverExpandButton
      to={constructDetailsDialogLocation({
        cta: EXPAND_CTA,
        notesFocus: 'true',
      })}
    />
  )
}

/** Mounted only while the popover is open, so it focuses the composer. */
function PostNotesPopoverThread({
  expand,
}: {
  expand: ReactNode
}): JSX.Element {
  const post = usePostData()
  const canCreate = post.allowedActions.includes('addPostNote')
  const frame = usePostNotesFrame({
    postId: post.id,
    channelId: post.channel.id,
    canCreate,
    focusComposer: true,
  })

  return (
    <PostNotesProvider {...frame.providerProps}>
      <NotesPopoverHeader>
        <NotesPopoverTitle>
          <SplitTagBadge splitName={COLLABORATION_NOTES_SPLIT} />
          <NotesVisibilityHint buttonRef={frame.infoButtonRef} />
        </NotesPopoverTitle>
        <NotesPopoverActions>
          {expand}
          <NotesFeedActions />
        </NotesPopoverActions>
      </NotesPopoverHeader>
      <PostNotesFeed />
      {canCreate && <CreatePostNoteForm fieldRef={frame.createFieldRef} />}
      <FocusAfterDelete
        requested={frame.focusAfterDeleteRequested}
        onHandled={frame.clearFocusAfterDelete}
        focusCreate={frame.focusCreate}
      />
    </PostNotesProvider>
  )
}
